import { readRecordedFormat } from '../../../vendor/client/messaging/recordedFormat.js';
import { useCallback, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { getMyVault } from '../vault/vaultApi';
import { createMessage, presignUpload, putCiphertext, finalizeMedia, configureTrigger, createTriggerGroup, makeSealApiClient } from './sealApi';
import { runSealFlow } from './sealClient';
import type { MessageDraft } from '../../screens/ScheduleView';

export type SealStatus = 'idle' | 'sealing' | 'sealed' | 'error';

/**
 * Orchestrates the whole seal for a recorded message: encrypt (vendored,
 * proven `runSealFlow`) → upload the ciphertext to R2 via the presigned PUT
 * → finalize the media row → seal → configure the delivery trigger.
 *
 * The vendored `runSealFlow` owns CEK generation, AES-GCM encryption, and
 * both key wraps (recipient + vault DEK) — this hook only supplies the API
 * adapter and the `onCiphertextReady` upload hook.
 *
 * Retries are idempotent: `messageIdRef`/`sealedRef` survive re-renders so a
 * retry of the SAME draft (same `blob` identity) reuses the existing draft
 * message and resumes from the failed step instead of creating a new draft
 * or re-running the encrypt/upload/seal pipeline. A retry with a NEW draft
 * (different `blob`) resets both refs and starts fresh. This is safe because
 * `seal-context` is draft-only and `seal` itself is a server-side CAS (a
 * double-seal 409s rather than corrupting state).
 */
export function useSealFlow(): { status: SealStatus; error: string | null; seal(draft: MessageDraft): Promise<void> } {
  const { api } = useAuth();
  const [status, setStatus] = useState<SealStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const messageIdRef = useRef<string | null>(null);
  const sealedRef = useRef(false);
  const triggersConfiguredRef = useRef(false);
  const groupIdRef = useRef<string | null>(null);
  const configuredCountRef = useRef(0);
  const draftKeyRef = useRef<Blob | null>(null);

  const seal = useCallback(
    async (draft: MessageDraft) => {
      setStatus('sealing');
      setError(null);
      try {
        const isLetter = draft.format === 'letter';
        // Retry identity: the blob (video) or the composed text (letter). A new
        // draft resets refs; a retry of the same draft resumes.
        const draftKey = (isLetter ? draft.letterText : draft.blob) as unknown as Blob | null;
        if (draftKeyRef.current !== draftKey) {
          messageIdRef.current = null;
          sealedRef.current = false;
          triggersConfiguredRef.current = false;
          groupIdRef.current = null;
          configuredCountRef.current = 0;
          draftKeyRef.current = draftKey;
        }

        const dashboard = await getMyVault(api);
        if (messageIdRef.current === null) {
          const created = await createMessage(api, {
            vaultId: dashboard.vault.id,
            recipientId: draft.recipientId,
            format: isLetter ? 'letter' : 'video',
          });
          messageIdRef.current = created.message.id;
        }
        const messageId = messageIdRef.current;

        if (!sealedRef.current) {
          if (isLetter) {
            // Letter: the ciphertext travels inline with the seal (runSealFlow
            // handles the letter branch) — no R2 upload, so no onCiphertextReady.
            const letterBytes = new TextEncoder().encode(draft.letterText ?? '');
            await runSealFlow({
              api: makeSealApiClient(api),
              messageId,
              contentPlaintext: letterBytes,
            });
          } else {
            const videoBytes = new Uint8Array(await draft.blob!.arrayBuffer());
            // Read the real codec off the recording rather than asserting one.
            // The manifest this produces is the only thing a future decoder has.
            const format = readRecordedFormat(draft.blob!.type, 'video');
            const durationSeconds = Math.max(1, Math.round((draft.durationMs ?? 1000) / 1000));

            await runSealFlow({
              api: makeSealApiClient(api),
              messageId,
              contentPlaintext: videoBytes,
              onCiphertextReady: async ({ ciphertext, iv }: { ciphertext: Uint8Array; iv: Uint8Array }) => {
                const presigned = await presignUpload(api, messageId, ciphertext.byteLength);
                await putCiphertext({ url: presigned.url, headers: presigned.requiredHeaders, body: ciphertext });
                await finalizeMedia(api, messageId, { contentLengthBytes: ciphertext.byteLength, codec: format.codec, container: format.container, durationSeconds, ivB64: bytesToBase64(iv) });
              },
            });
          }
          sealedRef.current = true;
        }

        // Configure the delivery trigger(s) once. A retry after a PARTIAL
        // failure must be resume-safe: reuse the already-created group and only
        // attach the conditions that didn't land yet — otherwise a retry would
        // create a SECOND group + re-attach condition 1, leaving an orphan
        // one-member group that (under AND) delivers on condition 1 alone.
        if (!triggersConfiguredRef.current) {
          const conditions = draft.conditions;
          if (conditions !== undefined && conditions.length > 1) {
            if (groupIdRef.current === null) {
              const group = await createTriggerGroup(api, dashboard.vault.id, {
                combinator: draft.combinator ?? 'AND',
              });
              groupIdRef.current = group.trigger_group.id;
            }
            for (let i = configuredCountRef.current; i < conditions.length; i += 1) {
              await configureTrigger(api, messageId, {
                condition: conditions[i]!,
                triggerTimezone: 'UTC',
                triggerGroupId: groupIdRef.current,
              });
              configuredCountRef.current = i + 1;
            }
          } else {
            await configureTrigger(api, messageId, {
              condition: draft.condition ?? { kind: 'date', date: draft.deliveryDate },
              triggerTimezone: 'UTC',
            });
          }
          triggersConfiguredRef.current = true;
        }
        setStatus('sealed');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'We could not seal this message.');
        setStatus('error');
      }
    },
    [api],
  );

  return { status, error, seal };
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}
